import { useState, useEffect } from 'react';
import { Paper, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CustomOrderArrow from '../common/IconosOrdenacion';
import { formatEuro } from '../../functions/formatNumber';
import { getColorForAmount } from '../../functions/colors';

const AnualBalanceListGlobal = ({ years, gotoDetail, defaultViewMode = 'estalvi'}) => {

  const [sortConfig, setSortConfig] = useState({ key: 'anio', direction: 'desc' });
  const [viewMode, setViewMode] = useState(defaultViewMode);

  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [defaultViewMode]);

  const listYears = Object.values(years).filter(y => y!=null);

  // Función para manejar la ordenación
  const handleSort = (event, key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    event.stopPropagation();
  };

  const handleToggleViewMode = (e) => {
    setViewMode(prev => {
      if (prev === 'estalvi') return 'inversiones';
      if (prev === 'inversiones') return 'actual';
      return 'estalvi';
    });
    e.stopPropagation();
  };

  // Función para ordenar los balances según el criterio
  const sortedBalances = [...listYears].sort((a, b) => {
    // Comprobar la clave de ordenación actual
    switch (sortConfig.key) {
      case 'anio':
        // Ordenar por el número de anio (anio.numero)
        return sortConfig.direction === 'asc'
          ? a.anio - b.anio
          : b.anio - a.anio;

      case 'numMoviments':
        // Ordenar por el número de movimientos (movimientos.num)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.numMoviments - b.importAgregat.numMoviments
          : b.importAgregat.numMoviments - a.importAgregat.numMoviments;

      case 'ingressos':
        // Ordenar por ingresos (importes.ingresos)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.ingressos - b.importAgregat.ingressos
          : b.importAgregat.ingressos - a.importAgregat.ingressos;

      case 'despeses':
        // Ordenar por gastos (importes.gastos)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.despeses - b.importAgregat.despeses
          : b.importAgregat.despeses - a.importAgregat.despeses;

      case 'saldo':
        // Ordenar por saldo según el modo
        const getSaldoVal = (item) => {
          if (viewMode === 'estalvi') return item.importAgregat.saldoEstalvi;
          if (viewMode === 'inversiones') return item.importAgregat.inversions;
          return item.importAgregat.saldo;
        };
        return sortConfig.direction === 'asc'
          ? getSaldoVal(a) - getSaldoVal(b)
          : getSaldoVal(b) - getSaldoVal(a);

      default:
        return 0; // Si no hay un criterio de ordenación válido
    }
  });
  
  return (
    <Paper sx={{ padding: '20px' }}>
        {/* Cabecera del Grid */}
        <Grid container spacing={2} alignItems="center" sx={{ 
          marginBottom: 2, 
          fontWeight: 'bold', 
          backgroundColor: '#f0f0f0', // Fondo claro
          color: '#000', // Texto oscuro
          paddingTop: '2px', 
          paddingBottom: '2px',
          paddingLeft: '10px',
          paddingRight: '10px',
          borderBottom: '2px solid #ddd' // Borde inferior
        }}>
          <Grid size={{xs: 12, sm: 4 }}>
            <Typography variant="body2">
              <span onClick={(e) => handleSort(e, 'anio')}>
                Any {sortConfig.key === 'anio' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>{' '}
              <span style={{ fontSize: '0.8rem', color: 'gray' }} onClick={(e) => handleSort(e, 'numMoviments')}>
                (Num.Moviments) {sortConfig.key === 'numMoviments' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>
            </Typography>
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center">
            {viewMode !== 'inversiones' && (
              <Typography variant="body2">
                <span onClick={(e) => handleSort(e, 'ingressos')}>
                  Ingressos {sortConfig.key === 'ingressos' && <CustomOrderArrow direction={sortConfig.direction}/> }
                </span>{' / '}
                <span onClick={(e) => handleSort(e, 'despeses')}>
                  Despeses {sortConfig.key === 'despeses' && <CustomOrderArrow direction={sortConfig.direction}/> }
                </span>
              </Typography>
            )}
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="row" alignItems="center" justifyContent="flex-end" 
            sx={{ gap: '1px' }}>
            <IconButton onClick={handleToggleViewMode} size="small" title="Canviar vista de saldo" sx={{ marginRight: '5px' }}>
              <SwapHorizIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <Typography variant="body2" onClick={(e) => handleSort(e, 'saldo')}>
              {viewMode === 'estalvi' ? "Saldo Estalvi" : viewMode === 'inversiones' ? "Inversions" : "Saldo de l'Any"} {sortConfig.key === 'saldo' && <CustomOrderArrow direction={sortConfig.direction}/> }
            </Typography>
          </Grid>
        </Grid>    
        
        {/* List Anys */}
        { sortedBalances.map((value) =>  {
          const saldoVal = viewMode === 'estalvi' 
            ? value.importAgregat.saldoEstalvi 
            : viewMode === 'inversiones' 
              ? value.importAgregat.inversions 
              : value.importAgregat.saldo;

          const colorSaldo = getColorForAmount(saldoVal, viewMode === 'inversiones');

          return (
            <Grid container 
              key={value.anio} 
              spacing={2} 
              alignItems="center" 
              onClick={()=>gotoDetail(value.anio)}
              sx={{ 
                marginBottom: 1,
                paddingLeft: '10px',
                paddingRight: '10px',
                transition: 'background-color 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)', // Cambia el color de fondo al pasar el ratón
                },
            }}>

              {/* Columna izquierda: Número del anio, nombre y número de movimientos */}
              <Grid size={{xs: 12, sm: 4 }}>
                <Typography variant="body1">
                  <span >{value.anio}</span>{' '}
                  <span style={{ fontSize: '0.8rem', color: 'gray' }}>
                  ({value.importAgregat.numMoviments})
                  </span>
                </Typography>
              </Grid>

              {/* Columna central: Importe ingresado y gastado */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center">
                {viewMode !== 'inversiones' ? (
                  <Typography variant="body2">
                    <span style={{ color: 'green' }}>
                      {formatEuro(value.importAgregat.ingressos)}
                    </span>{' / '}
                    <span style={{ color: 'red' }}>
                      {formatEuro(value.importAgregat.despeses)}
                    </span>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="gray" sx={{ fontStyle: 'italic' }}>
                    -
                  </Typography>
                )}            
              </Grid>

              {/* Columna derecha: Saldo del anio y saldo acumulado */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="flex-end" 
                sx={{ gap: '1px' }}>
                <Typography variant="body1" fontWeight="bold" color={colorSaldo} >
                  {formatEuro(saldoVal)}
                </Typography>
              </Grid>
            </Grid>
          );
        }
      )}
    </Paper>
    );
};

export default AnualBalanceListGlobal;
